import { useState } from 'react';

// ── Admin: reassign-session-track modal ───────────────────────────────────────
// Corrects a GPS mis-attribution by recomputing one session's entries against the
// track it was actually driven on. Unlike a merge it moves only this session, not
// every session on the source track. Use for co-located facilities (Summit Point,
// Pocono MEGA/N.S.3) where overlapping layouts get confused at upload.
export default function ReassignTrackModal({
  sessionId, driver, lapTimeStr, vehicleName, eventDate,
  currentTrackName, tracks, onReassign, onClose,
}) {
  const [target, setTarget] = useState('');
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState('');
  const [done, setDone]     = useState(null);   // result {entries_written, track_id}

  const doReassign = async () => {
    if (!target) return;
    setBusy(true); setError('');
    try {
      const res = await onReassign(sessionId, target);
      setDone(res);
    } catch (e) {
      setError(e.message || 'Reassign failed');
    } finally {
      setBusy(false);
    }
  };

  const ov = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  };
  const panel = {
    background: '#1c1f26', color: '#e6e6e6', border: '1px solid #353a45',
    borderRadius: 10, padding: 20, width: 'min(520px, 92vw)',
    maxHeight: '86vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
  };
  const sel = {
    background: '#13151a', color: '#e6e6e6', border: '1px solid #353a45',
    borderRadius: 6, padding: '6px 8px', minWidth: 200,
  };
  const btn = {
    background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 6,
    padding: '7px 14px', cursor: 'pointer', fontWeight: 600,
  };

  return (
    <div style={ov} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Reassign session <span style={{ opacity: 0.6, fontSize: 13 }}>(admin)</span></h2>
          <button onClick={onClose} style={{ ...sel, minWidth: 0, cursor: 'pointer' }}>✕</button>
        </div>

        {done ? (
          <>
            <p style={{ marginTop: 0, fontSize: 14, lineHeight: 1.5 }}>
              ✓ Recomputed <strong>{done.entries_written}</strong> {done.entries_written === 1 ? 'lap' : 'laps'} onto
              {' '}<strong>{tracks.find(t => t.track_id === done.track_id)?.track_name || done.track_id}</strong>.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button style={btn} onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <p style={{ marginTop: 0, opacity: 0.75, fontSize: 13, lineHeight: 1.5 }}>
              Recompute this session's laps against the circuit it was actually driven on.
              Use when GPS auto-detection picked the wrong layout at a multi-config facility
              (e.g. Summit Point's Main / Jefferson / Shenandoah, all within ~1&nbsp;km). Only
              <em> this</em> session moves — other sessions on the current track are untouched.
            </p>

            <div style={{ fontSize: 13, marginBottom: 12, padding: '8px 10px', background: '#13151a', borderRadius: 6, border: '1px solid #2a2e37' }}>
              <div><span style={{ opacity: 0.6 }}>Driver:</span> {driver}</div>
              <div><span style={{ opacity: 0.6 }}>Lap:</span> {lapTimeStr} · {vehicleName || '—'} · {eventDate || '—'}</div>
              <div><span style={{ opacity: 0.6 }}>Currently on:</span> {currentTrackName || '—'}</div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
              <span>Move to</span>
              <select style={sel} value={target} onChange={e => setTarget(e.target.value)} disabled={busy}>
                <option value="">— correct track —</option>
                {tracks.map(t => (
                  <option key={t.track_id} value={t.track_id}>{t.track_name} ({t.track_id})</option>
                ))}
              </select>
              <button
                style={{ ...btn, opacity: (!target || busy) ? 0.5 : 1 }}
                onClick={doReassign}
                disabled={!target || busy}
              >{busy ? 'Recomputing…' : 'Reassign'}</button>
            </div>

            {error && <div style={{ color: '#ff7a7a', fontSize: 13 }}>⚠ {error}</div>}
          </>
        )}
      </div>
    </div>
  );
}
